import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { Checkbox } from '@/shared/components/Checkbox'
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  AlertCircle,
  CheckIcon,
  CircleCheckBig,
} from "lucide-react";
import ProviderLoginImg from '@/assets/provider-login.webp';
import bigLogo from '@/assets/big-logo.svg';
import CheckProviderStep3 from '@/assets/Step3-provider.svg';
import { supplierAuthService } from '@/features/proveedores/api/supplierAuthService';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

const RFC_REGEX = /^([A-ZÑ&]{3,4})([0-9]{2}[0-1][0-9][0-3][0-9])([A-Z0-9]{3})$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface LoginProviderProps {
  onLogin: (
    email: string,
    password: string
  ) => Promise<{ firstName: string; lastName: string } | void>;
  isLoading: boolean;
  error: string | null;
}

const RegisterProviders = ({
  isLoading: _externalLoading,
  error: externalError,
}: LoginProviderProps) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    rfc: "",
    razonSocial: "",
    email: "",
    phone: "",
    providerType: "corporate",
    serviceType: "1",
    password: "",
    confirmPassword: "",
    mainContactName: "",
    salesEmail: "",
    accountsPayableEmail: "",
    internalRequesterName: "",
    acceptTerms: false,
    country: "México",
    state: "",
    city: "",
    municipality: "",
    suburb: "",
    postalCode: "",
    street: "",
    externalNumber: "",
    internalNumber: "",
    repseNumber: "",
    vatType: "General",
  });
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const [touchedFields, setTouchedFields] = useState({
    rfc: false,
    email: false,
  });

  const validateRFC = (rfc: string) => RFC_REGEX.test(rfc);
  const validateEmail = (email: string) => EMAIL_REGEX.test(email);

  const isStep1Valid =
    formData.mainContactName.trim().length > 0 &&
    validateRFC(formData.rfc) &&
    validateEmail(formData.email);

  const isStep2Valid =
    Object.values(passwordValidation).every(Boolean) &&
    formData.password === formData.confirmPassword &&
    formData.password.length > 0 &&
    formData.acceptTerms;

  const steps = [
    { id: 1, name: "Datos de cuenta" },
    { id: 2, name: "Contraseña" },
    { id: 3, name: "Confirmación" },
  ];

  const validatePassword = (password: string) => {
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    let { value } = e.target;

    // Auto-uppercase RFC
    if (name === "rfc") {
      value = value.toUpperCase().replace(/[^A-Z0-9Ñ&]/g, "");
    }

    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate password in real-time
    if (name === "password") {
      validatePassword(value);
    }

    if (localError) setLocalError(null);
    if (successMessage) setSuccessMessage(null);
  };

  const handleSubmit = async () => {
    if (currentStep === 2) {
      // Validaciones antes de enviar
      const allPasswordRulesMet = Object.values(passwordValidation).every(
        (rule) => rule === true
      );

      if (!allPasswordRulesMet) {
        setLocalError(
          "La contraseña no cumple con todos los requisitos de seguridad"
        );
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setLocalError("Las contraseñas no coinciden");
        return;
      }
      if (!formData.acceptTerms) {
        setLocalError("Debes aceptar los términos y condiciones");
        return;
      }

      setIsLoading(true);
      setLocalError(null);

      try {
        const registerData = {
          mainContactName: formData.mainContactName,
          rfc: formData.rfc,
          //legalName: formData.razonSocial,
          //personType: formData.providerType,
          //vatType: formData.vatType,
          //serviceTypeId: formData.serviceType,
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          //mainContactPhone: formData.phone,
          acceptTerms: formData.acceptTerms,
        };

        const response = await supplierAuthService.register(registerData);

        setSuccessMessage(response.message);
        setCurrentStep(3);
      } catch (errCrudo: unknown) {
        const err = errCrudo as {
          message?: string;
          statusCode?: number;
          status?: number;
          error?: string;
          response?: { status?: number; data?: { message?: string } };
        };
        setLocalError(err.message || "Error al registrar el proveedor");
      } finally {
        setIsLoading(false);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const displayError = externalError || localError;

  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {/* Left Side - Form Section */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
        <div className="w-full max-w-md">
          {/* Logo - Centrado y Grande */}
          <div className="mb-4 flex flex-col items-center">
            <img src={bigLogo} alt="SLYRSA Logo" className="h-14 w-auto mb-2" />
          </div>
          {/* Welcome Text */}
          <div className="mb-4 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Crea tu cuenta
            </h1>
            <p className="text-sm text-gray-600 text-left">
              Llena el siguiente formulario con los datos solicitados para crear
              tu cuenta.
            </p>
          </div>

          {/*Stepper Indicator */}
          <div className="flex items-start justify-center my-5">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-start">
                <div className="w-[110px] flex flex-col items-center">
                  {/* Círculo */}
                  <div
                    className={`w-[32px] h-[32px] rounded-full border-2 flex items-center justify-center transition-all
              ${step.id == currentStep
                ? "border-[#315FB9] bg-blue-600/10"
                : step.id < currentStep && currentStep !== 3
                  ? "border-[#315FB9] bg-[#315FB9] cursor-pointer"
                  : step.id < currentStep
                    ? "border-[#315FB9] bg-[#315FB9]"
                    : "border-gray-300 bg-white"
              }`}
                    onClick={() => {
                      if (step.id < currentStep && currentStep !== 3) {
                        setCurrentStep(1);
                      }
                    }}
                  >
                    {step.id == currentStep && (
                      <div className="w-[10px] h-[10px] bg-[#315FB9] rounded-full" />
                    )}
                    {step.id < currentStep && (
                      <CheckIcon className="w-[20px] h-[20px] bg-[#315FB9] rounded-full text-white" />
                    )}
                  </div>
                  <p className="mt-2 text-[11px] text-gray-600 text-center leading-tight">
                    {step.name}
                  </p>
                </div>

                {/* Línea conectora */}
                {index < steps.length - 1 && (
                  <div
                    className={`w-[48px] h-[2px] mt-[16px] transition-all ${step.id < currentStep ? "bg-[#315FB9]" : "bg-gray-300"
                      }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Error Message */}
          {displayError && !successMessage && (
            <div className="mb-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="ml-3 text-sm text-red-800">{displayError}</p>
              </div>
            </div>
          )}

          {/*Step Form 1 */}
          {currentStep == 1 && (
            <main className="overflow-y-auto h-auto p-1 max-h-[300px] xl:max-h-[350px] 2xl:max-h-[630px]">
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Full Name Field */}
                <div>
                  <label
                    htmlFor="mainContactName"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Nombre completo*
                  </label>
                  <input
                    id="mainContactName"
                    name="mainContactName"
                    type="text"
                    autoComplete="mainContactName"
                    required
                    value={formData.mainContactName}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-0  rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                    placeholder="Ingresa tu nombre completo"
                  />
                </div>
                {/* RFC Field */}
                <div>
                  <label
                    htmlFor="rfc"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    RFC*
                  </label>
                  <input
                    id="rfc"
                    name="rfc"
                    type="text"
                    autoComplete="rfc"
                    required
                    value={formData.rfc}
                    onChange={handleChange}
                    onBlur={() => setTouchedFields({ ...touchedFields, rfc: true })}
                    className={`w-full px-4 py-2.5 border rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400 uppercase placeholder:normal-case ${touchedFields.rfc && !validateRFC(formData.rfc)
                      ? "border-red-300"
                      : "border-transparent focus:border-[#315FB9]"
                      }`}
                    placeholder="Ingresa tu RFC"
                  />
                  {touchedFields.rfc && !validateRFC(formData.rfc) && (
                    <p className="mt-1 text-[10px] text-red-500">
                      Formato de RFC inválido
                    </p>
                  )}
                </div>

                {/* Razon social Field */}
                {/* <div>
                  <label
                    htmlFor="razonSocial"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Razón Social*
                  </label>
                  <input
                    id="razonSocial"
                    name="razonSocial"
                    type="text"
                    autoComplete="razonSocial"
                    required
                    value={formData.razonSocial}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-0  rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                    placeholder="Ingresa la razón social"
                  />
                </div> */}

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Correo electrónico*
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={() => setTouchedFields({ ...touchedFields, email: true })}
                    className={`w-full px-4 py-2.5 border rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400 ${touchedFields.email && !validateEmail(formData.email)
                      ? "border-red-300"
                      : "border-transparent focus:border-[#315FB9]"
                      }`}
                    placeholder="Ingresa tu correo electrónico"
                  />
                  {touchedFields.email && !validateEmail(formData.email) && (
                    <p className="mt-1 text-[10px] text-red-500">
                      Correo electrónico inválido
                    </p>
                  )}
                </div>

                {/* Phone Field */}
                {/* <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Teléfono*
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-0  rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                    placeholder="Ingresa tu teléfono"
                  />
                </div> */}

                {/* Provider Type Field */}
                {/* <div>
                  <label
                    htmlFor="providerType"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Tipo de Proveedor
                  </label>
                  <select
                    name="providerType"
                    value={formData.providerType}
                    onChange={(e) =>
                      setFormData({ ...formData, providerType: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border-0  rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                  >
                    <option value="corporate">Persona Moral</option>
                    <option value="individual">Persona Física</option>
                  </select>
                </div> */}

                {/* Service Type Field */}
                {/* <div>
                  <label
                    htmlFor="serviceType"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Tipo de Servicio
                  </label>
                  <select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={(e) =>
                      setFormData({ ...formData, serviceType: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border-0  rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                  >
                    <option value="">Seleccionar tipo de servicio</option>
                    {SERVICE_TYPES.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div> */}

                {/* Next step Button */}
                <button
                  type="button"
                  disabled={isLoading || !isStep1Valid}
                  className={estiloDeBoton('primario', 'w-full my-8')}
                  onClick={() => setCurrentStep(currentStep + 1)}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Enviando datos...
                    </div>
                  ) : (
                    "Siguiente"
                  )}
                </button>
              </form>
            </main>
          )}

          {/* Step Form 2 */}
          {currentStep == 2 && (
            <main className="overflow-y-auto h-auto xl:max-h-[350px] 2xl:max-h-[630px]">
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Contraseña*
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border-0  rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Password Rules */}
                <div className="text-sm text-gray-600 mx-6 flex flex-wrap justify-between max-w-[380px] space-y-2">
                  <p className="flex items-center">
                    {passwordValidation.minLength ? (
                      <CircleCheckBig className="w-[16px] h-[16px] text-[#1FD361] mr-2" />
                    ) : (
                      <div className="w-[16px] h-[16px] border-2 border-gray-300 rounded-full mr-2" />
                    )}
                    Al menos 8 caracteres
                  </p>
                  <p className="flex items-center">
                    {passwordValidation.hasUpperCase ? (
                      <CircleCheckBig className="w-[16px] h-[16px] text-[#1FD361] mr-2" />
                    ) : (
                      <div className="w-[16px] h-[16px] border-2 border-gray-300 rounded-full mr-2" />
                    )}
                    Letra mayúscula
                  </p>
                  <p className="flex items-center">
                    {passwordValidation.hasLowerCase ? (
                      <CircleCheckBig className="w-[16px] h-[16px] text-[#1FD361] mr-2" />
                    ) : (
                      <div className="w-[16px] h-[16px] border-2 border-gray-300 rounded-full mr-2" />
                    )}
                    Letra minúscula
                  </p>
                  <p className="flex items-center w-[138px] pl-4">
                    {passwordValidation.hasNumber ? (
                      <CircleCheckBig className="w-[16px] h-[16px] text-[#1FD361] mr-2" />
                    ) : (
                      <div className="w-[16px] h-[16px] border-2 border-gray-300 rounded-full mr-2" />
                    )}
                    Número
                  </p>
                  <p className="flex items-center">
                    {passwordValidation.hasSpecialChar ? (
                      <CircleCheckBig className="w-[16px] h-[16px] text-[#1FD361] mr-2" />
                    ) : (
                      <div className="w-[16px] h-[16px] border-2 border-gray-300 rounded-full mr-2" />
                    )}
                    Caracter especial
                  </p>
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-900 mb-2"
                  >
                    Confirmar Contraseña*
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border-0  rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                      placeholder="••••••"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember Me */}
                <div className="flex items-center text-sm space-x-2">
                  <Checkbox
                    id="acceptTerms"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        acceptTerms: e.target.checked,
                      })
                    }
                  />
                  <label
                    htmlFor="acceptTerms"
                    className="ml-2 block text-gray-700"
                  >
                    Acepto términos y condiciones
                  </label>
                  <button
                    type="button"
                    className="text-[#3B5998] hover:text-[#344e86] underline"
                    onClick={() => setShowTermsModal(true)}
                  >
                    (consultar)
                  </button>
                </div>

                {/* Next step Button */}
                <button
                  type="button"
                  disabled={isLoading || !isStep2Valid}
                  className={estiloDeBoton('primario', 'w-full my-8')}
                  onClick={handleSubmit}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Enviando datos...
                    </div>
                  ) : (
                    "Siguiente"
                  )}
                </button>
              </form>
            </main>
          )}

          {/* Step Form 3 */}
          {currentStep == 3 && (
            <main className="overflow-y-auto h-auto flex flex-col justify-center items-center">
              <div className="my-10 flex flex-col items-center justify-center">
                <img
                  src={CheckProviderStep3}
                  alt="Proveedor Registrado"
                  className=" w-[137px] h-auto"
                />
                <p className="w-[280px] text-center mt-6">
                  Revisa tu correo y utiliza el enlace enviado para iniciar
                  sesión.
                </p>
              </div>

              {/* Next step Button */}
              <button
                type="button"
                disabled={isLoading}
                className={estiloDeBoton('primario', 'w-full my-2')}
                onClick={() => navigate("/proveedores/login")}
              >
                Ir al inicio de sesión
              </button>
            </main>
          )}

          {/* Forgot Password Link */}
          <div className="mt-4 flex justify-center space-x-2">
            <p className="text-sm hover:text-[#344e86] transition-colors">
              ¿Ya tienes cuenta?
            </p>
            <button
              onClick={() => navigate("/proveedores/login")}
              className="text-sm text-[#3B5998] hover:text-[#344e86] transition-colors"
            >
              Inicia sesión aquí
            </button>
          </div>
        </div>
      </div>

      {/* Right Side - Features Section with Gradient in Rounded Box */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-6 bg-gray-50">
        {/* Rounded White Container */}
        <div className="rounded-[32px] w-full h-[calc(100vh-3rem)] flex flex-col items-center relative overflow-hidden shadow-xl">
          {/* Features List - Más abajo */}
          <div className="relative z-30 w-full max-w-lg space-y-4">
            <div className="absolute top-5   sm:right-[20px] xl:right-[-50px] 2xl:right-[-180px] bg-white w-[154px] h-[35px] rounded-full flex justify-center items-center">
              <p className="font-semibold">Proveedor</p>
            </div>
            {/* Feature 1 */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-[500px] px-7 py-4 flex items-start space-x-3 absolute top-[130px] z-30 mt-12">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-4 h-4 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Administra, monitorea y da seguimiento a tus proveedores en un
                solo sistema.
              </p>
            </div>
          </div>

          {/* Tablet Image - Alineado al fondo */}
          <div className="relative z-10 w-full h-full">
            <div className="absolute inset-0 rounded-[32px] blur-2xl pointer-events-none bg-gradient-to-t from-[#1FB7EE]/10  to-[#315FB9]/80 "></div>
            <img
              src={ProviderLoginImg}
              alt="Login Proveedor"
              className=" w-full h-auto object-center"
            />
          </div>
        </div>
      </div>

      {showTermsModal && (
        <CapaDeModal>
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Términos y condiciones para proveedores
              </h2>
              <button
                type="button"
                className={estiloDeBoton('secundario')}
                onClick={() => setShowTermsModal(false)}
              >
                Cerrar
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-4 text-sm text-gray-700 space-y-4">
              <p>
                Al registrarte como proveedor en SLYRSA aceptas que la
                información proporcionada será utilizada para validar tu alta y
                gestionar la relación comercial.
              </p>
              <p>
                Debes proporcionar datos verídicos y mantenerlos actualizados.
                SLYRSA podrá rechazar o suspender registros con información
                incompleta, falsa o inconsistente.
              </p>
              <p>
                El uso de la plataforma implica aceptar las políticas internas
                de cumplimiento, auditoría y seguridad de la información.
              </p>
              <p>
                Tus datos serán tratados conforme a la normativa aplicable en
                materia de protección de datos personales.
              </p>
              <p className="text-xs text-gray-500">
                Nota: contenido temporal para frontend. Pendiente conectar con
                versión oficial publicada por backend.
              </p>
            </div>
            <div className="flex justify-end border-t px-6 py-4">
              <button
                type="button"
                className={estiloDeBoton('primario')}
                onClick={() => setShowTermsModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </div>
  );
};

export default RegisterProviders;
